/**
 * Error boundary specifically for authentication errors
 * Provides graceful fallback UI when auth context fails
 *
 * Features:
 * - Progressive disclosure of error details
 * - Multiple recovery actions (retry, logout, clear cache, go home)
 * - Copy error details for support
 * - Accessibility: role="alert", focus management, keyboard navigable
 * - WRA glassmorphism design system
 */

import { Component, ReactNode, ErrorInfo, createRef } from 'react';
import { isDevelopment } from '@/config/environment';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import { isChunkLoadError, recoverFromChunkLoad } from '@/utils/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  copied: boolean;
  retrying: boolean;
}

export class AuthErrorBoundary extends Component<Props, State> {
  private errorRef = createRef<HTMLDivElement>();
  private errorId: string;

  constructor(props: Props) {
    super(props);
    this.errorId = `err-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false,
      retrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AuthErrorBoundary caught error:', error);
    console.error('Error info:', errorInfo);

    // A lazy() route chunk that 404s after a deploy is caught here by Suspense,
    // not by the window handlers in main.tsx. Auto-reload for fresh assets
    // instead of showing "Session Interrupted" (whose retry would re-loop).
    if (isChunkLoadError(error.message)) {
      console.warn('Chunk load failed, auto-reloading for fresh assets');
      recoverFromChunkLoad();
      return;
    }

    const isAuthError =
      error.message?.includes('useAuth') ||
      error.message?.includes('AuthProvider') ||
      error.message?.includes('authentication') ||
      error.message?.includes('token');

    if (isAuthError) {
      console.error('Authentication error detected. Component:', errorInfo.componentStack);
    }

    this.setState({ error, errorInfo });
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.hasError && !prevState.hasError && this.errorRef.current) {
      this.errorRef.current.focus();
    }
  }

  handleRetry = () => {
    this.setState({ retrying: true });

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false,
      retrying: false,
    });

    window.location.reload();
  };

  handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.clear();
    } catch {
      // Storage access may fail in some contexts
    }
    window.location.href = '/login';
  };

  handleClearAndRetry = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage access may fail
    }
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleToggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const details = [
      `Error Reference: ${this.errorId}`,
      `Timestamp: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `User Agent: ${navigator.userAgent}`,
      `Error: ${error?.message || 'Unknown'}`,
      errorInfo?.componentStack ? `Component Stack: ${errorInfo.componentStack}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    navigator.clipboard.writeText(details).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, showDetails, copied, retrying } = this.state;

      return (
        <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
          <div className="max-w-lg w-full" ref={this.errorRef} tabIndex={-1} role="alert">
            {/* Glass card */}
            <div className="backdrop-blur-md bg-glass-medium border border-glass-medium rounded-[1.3rem] p-8 shadow-2xl">
              {/* Shield icon with pulse */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping" />
                  <div className="relative w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-amber-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 15v.01M12 9v3m0-9.75C6.615 2.25 3 6 3 6v6c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622V6s-3.615-3.75-9-3.75z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Heading */}
              <h2 className="text-2xl font-bold text-cyber-cyan mb-2 text-center">
                Session Interrupted
              </h2>
              <p className="text-gray-300 text-center mb-2">
                Your authentication session encountered an issue. This is usually temporary.
              </p>
              <p className="text-gray-500 text-xs text-center mb-6 font-mono">
                Ref: {this.errorId}
              </p>

              {/* Primary actions */}
              <div className="flex gap-3 mb-3">
                <UnifiedButton
                  onClick={this.handleRetry}
                  variant="primary"
                  className="flex-1"
                  disabled={retrying}
                  aria-label="Retry loading the page"
                >
                  {retrying ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="3"
                          className="opacity-25"
                        />
                        <path
                          d="M4 12a8 8 0 018-8"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                      </svg>
                      Retrying...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Try Again
                    </>
                  )}
                </UnifiedButton>
                <UnifiedButton
                  onClick={this.handleLogout}
                  variant="outline"
                  className="flex-1"
                  aria-label="Log out and return to login page"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Log Out
                </UnifiedButton>
              </div>

              {/* Secondary actions */}
              <div className="flex gap-3 mb-6">
                <UnifiedButton
                  onClick={this.handleClearAndRetry}
                  variant="ghost"
                  className="flex-1 text-sm"
                  aria-label="Clear cached data and retry"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Clear Cache & Retry
                </UnifiedButton>
                <UnifiedButton
                  onClick={this.handleGoHome}
                  variant="ghost"
                  className="flex-1 text-sm"
                  aria-label="Go to homepage"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Go Home
                </UnifiedButton>
              </div>

              {/* Troubleshooting tips */}
              <div className="bg-glass-light/20 border border-glass-light rounded-[1rem] p-4 mb-4">
                <p className="text-xs text-gray-400 font-medium mb-2">Quick troubleshooting:</p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-cyber-cyan mt-0.5">1.</span>
                    <span>
                      Try <strong className="text-gray-400">Try Again</strong> first — this
                      refreshes your session
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyber-cyan mt-0.5">2.</span>
                    <span>
                      If that fails, <strong className="text-gray-400">Log Out</strong> and sign
                      back in
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyber-cyan mt-0.5">3.</span>
                    <span>
                      Still stuck? <strong className="text-gray-400">Clear Cache & Retry</strong>{' '}
                      resets all stored data
                    </span>
                  </li>
                </ul>
              </div>

              {/* Progressive disclosure - error details */}
              <div className="border-t border-glass-light pt-4">
                <UnifiedButton
                  onClick={this.handleToggleDetails}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 w-full justify-start px-0"
                  aria-expanded={showDetails}
                  aria-controls="auth-error-details"
                  aria-label={`${showDetails ? 'Hide' : 'Show'} technical error details`}
                >
                  <svg
                    className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  {showDetails ? 'Hide' : 'Show'} technical details
                </UnifiedButton>

                {showDetails && (
                  <div
                    id="auth-error-details"
                    className="mt-3 p-4 bg-glass-light/10 border border-glass-light rounded-[1rem] space-y-3"
                  >
                    {error && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Error message:</p>
                        <p className="text-sm text-red-400 font-mono break-all">{error.message}</p>
                      </div>
                    )}

                    {isDevelopment && errorInfo?.componentStack && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Component stack:</p>
                        <pre className="text-xs text-gray-400 font-mono overflow-auto max-h-32 bg-dark-950/50 rounded-lg p-2">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    <UnifiedButton
                      onClick={this.handleCopyError}
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      aria-label="Copy error details to clipboard"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      {copied ? 'Copied to clipboard' : 'Copy error details for support'}
                    </UnifiedButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
